import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Save, Copy, Check, Zap, RefreshCw, Clock, Phone, Sparkles, RotateCcw, Plus, Trash2, MessageSquare, PhoneCall, XCircle, Shield, Calendar as CalendarIcon, Link2, Unplug, Send, Wand2, CheckCircle2, DollarSign, Wrench, AlertTriangle } from "lucide-react";
import ConfirmModal from "../components/ConfirmModal";
import { Section, Field } from "../components/settings/common";
import BookingSection from "../components/settings/BookingSection";
import IntegrationsSection from "../components/settings/IntegrationsSection";
import SequencesSection from "../components/settings/SequencesSection";

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [secretMeta, setSecretMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [testCallOpen, setTestCallOpen] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testing, setTesting] = useState(false);
  const [testSmsPhone, setTestSmsPhone] = useState("");
  const [testingSms, setTestingSms] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testingEmail, setTestingEmail] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [form, setForm] = useState({
    webhook_url: "",
    sequences_enabled: false,
    delay_first_sms_minutes: 5,
    delay_followup_call_hours: 24,
    delay_lost_after_hours: 72,
    vapi_assistant_id: "",
    vapi_phone_number_id: "",
    agent_name: "Sara",
    apollo_api_key: "",
    apify_api_token: "",
    sequence_steps: [],
    business_hours_start: 9,
    business_hours_end: 18,
    business_days_only: true,
    smart_scheduling: false,
    auto_qualify_mode: "suggest",
    tcpa_acknowledged: false,
    prompt_system: "",
    prompt_call_script: "",
    prompt_sms: "",
    prompt_email: "",
    calendar_work_start_hour: 9,
    calendar_work_end_hour: 18,
    calendar_workdays: [0, 1, 2, 3, 4],
    calendar_meeting_duration_min: 30,
    calendar_timezone: "America/New_York",
    sms_provider: "twilio",
    quo_api_key: "",
    quo_phone_number: "",
    quo_webhook_secret: "",
    sendgrid_api_key: "",
    sendgrid_from_email: "",
    sendgrid_from_name: "",
    sendgrid_reply_to: "",
    custom_angles: [],
    booking_logo_url: "",
    booking_headline: "",
    booking_subheadline: "",
    booking_description: "",
    booking_web_conf_note: "",
    booking_brand_color: "#002FA7",
    booking_questions: [],
    public_booking_slug: "",
    daily_spend_cap_usd: 0,
  });
  const [promptDefaults, setPromptDefaults] = useState({
    prompt_system: "",
    prompt_call_script: "",
    prompt_sms: "",
    prompt_email: "",
  });
  const [savingPrompts, setSavingPrompts] = useState(false);
  const [gcalStatus, setGcalStatus] = useState(null);
  const [gcalLoading, setGcalLoading] = useState(false);

  const loadGcalStatus = async () => {
    try {
      const { data } = await api.get("/oauth/google/calendar/status");
      setGcalStatus(data);
    } catch (e) {
      setGcalStatus({ configured: false, connected: false });
    }
  };

  const connectGoogleCalendar = async () => {
    setGcalLoading(true);
    try {
      const { data } = await api.get("/oauth/google/calendar/start");
      if (data?.auth_url) {
        window.location.href = data.auth_url;
        return;
      }
      toast.error("No auth URL returned");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start Google OAuth");
    } finally {
      setGcalLoading(false);
    }
  };

  const disconnectGoogleCalendar = async () => {
    setConfirmModal({
      title: "Disconnect Google Calendar?",
      body: "The AI agent will stop booking demos until you reconnect. Existing events remain in Google.",
      destructive: true,
      confirmLabel: "Disconnect",
      onConfirm: async () => {
        setGcalLoading(true);
        try {
          await api.post("/oauth/google/calendar/disconnect");
          toast.success("Google Calendar disconnected");
          await loadGcalStatus();
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || "Disconnect failed");
        } finally {
          setGcalLoading(false);
        }
      },
    });
  };

  // Surface ?google_calendar=connected|error and refresh status on mount.
  useEffect(() => {
    loadGcalStatus();
    const params = new URLSearchParams(window.location.search);
    const flag = params.get("google_calendar");
    if (flag === "connected") {
      toast.success("Google Calendar connected ✓");
    } else if (flag === "error") {
      toast.error(`Google Calendar error: ${params.get("error") || "unknown"}`);
    }
    if (flag) {
      // Clean up URL so a refresh doesn't re-trigger the toast.
      const url = new URL(window.location.href);
      url.searchParams.delete("google_calendar");
      url.searchParams.delete("error");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  const toggleWorkday = (d) => {
    setForm((s) => {
      const cur = Array.isArray(s.calendar_workdays) ? s.calendar_workdays : [];
      const next = cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d].sort();
      return { ...s, calendar_workdays: next };
    });
  };

  const saveCalendarPrefs = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        calendar_work_start_hour: form.calendar_work_start_hour,
        calendar_work_end_hour: form.calendar_work_end_hour,
        calendar_workdays: form.calendar_workdays,
        calendar_meeting_duration_min: form.calendar_meeting_duration_min,
        calendar_timezone: form.calendar_timezone,
      });
      toast.success("Calendar booking preferences saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
      setTestPhone(data.test_phone || "");
      // SEC-001 — sensitive fields come back as {set:true,last4:'…'}
      // descriptors, never the raw secret. The input starts empty and
      // the descriptor is stashed on secretMeta so we can show a
      // "Set · ••••1234" placeholder.
      const isDescriptor = (v) => v && typeof v === "object" && "set" in v;
      const raw = (v) => (isDescriptor(v) ? "" : v || "");
      setSecretMeta({
        vapi_api_key: isDescriptor(data.vapi_api_key) ? data.vapi_api_key : null,
        twilio_auth_token: isDescriptor(data.twilio_auth_token) ? data.twilio_auth_token : null,
        quo_api_key: isDescriptor(data.quo_api_key) ? data.quo_api_key : null,
        quo_webhook_secret: isDescriptor(data.quo_webhook_secret) ? data.quo_webhook_secret : null,
        sendgrid_api_key: isDescriptor(data.sendgrid_api_key) ? data.sendgrid_api_key : null,
        apollo_api_key: isDescriptor(data.apollo_api_key) ? data.apollo_api_key : null,
        apify_api_token: isDescriptor(data.apify_api_token) ? data.apify_api_token : null,
        google_places_api_key: isDescriptor(data.google_places_api_key) ? data.google_places_api_key : null,
      });
      setForm({
        webhook_url: data.webhook_url || data.default_webhook_url || "",
        sequences_enabled: data.sequences_enabled,
        delay_first_sms_minutes: data.delay_first_sms_minutes,
        delay_followup_call_hours: data.delay_followup_call_hours,
        delay_lost_after_hours: data.delay_lost_after_hours,
        vapi_assistant_id: data.vapi_assistant_id || data.vapi_default_assistant_id || "",
        vapi_phone_number_id: data.vapi_phone_number_id || data.vapi_default_phone_number_id || "",
        agent_name: data.agent_name || "Sara",
        apollo_api_key: raw(data.apollo_api_key),
        apify_api_token: raw(data.apify_api_token),
        sequence_steps: Array.isArray(data.sequence_steps) ? data.sequence_steps : [],
        business_hours_start: data.business_hours_start ?? 9,
        business_hours_end: data.business_hours_end ?? 18,
        business_days_only: data.business_days_only ?? true,
        smart_scheduling: data.smart_scheduling ?? false,
        auto_qualify_mode: data.auto_qualify_mode || "suggest",
        tcpa_acknowledged: data.tcpa_acknowledged ?? false,
        prompt_system: data.prompt_system || "",
        prompt_call_script: data.prompt_call_script || "",
        prompt_sms: data.prompt_sms || "",
        prompt_email: data.prompt_email || "",
        calendar_work_start_hour: data.calendar_work_start_hour ?? 9,
        calendar_work_end_hour: data.calendar_work_end_hour ?? 18,
        calendar_workdays: Array.isArray(data.calendar_workdays) ? data.calendar_workdays : [0,1,2,3,4],
        calendar_meeting_duration_min: data.calendar_meeting_duration_min ?? 30,
        calendar_timezone: data.calendar_timezone || "America/New_York",
        sms_provider: data.sms_provider || "twilio",
        quo_api_key: raw(data.quo_api_key),
        quo_phone_number: data.quo_phone_number || "",
        quo_webhook_secret: raw(data.quo_webhook_secret),
        sendgrid_api_key: raw(data.sendgrid_api_key),
        sendgrid_from_email: data.sendgrid_from_email || "",
        sendgrid_from_name: data.sendgrid_from_name || "",
        sendgrid_reply_to: data.sendgrid_reply_to || "",
        custom_angles: Array.isArray(data.custom_angles) ? data.custom_angles : [],
        booking_logo_url: data.booking_logo_url || "",
        booking_headline: data.booking_headline || "",
        booking_subheadline: data.booking_subheadline || "",
        booking_description: data.booking_description || "",
        booking_web_conf_note: data.booking_web_conf_note || "",
        booking_brand_color: data.booking_brand_color || "#002FA7",
        booking_questions: Array.isArray(data.booking_questions) ? data.booking_questions : [],
        public_booking_slug: data.public_booking_slug || "",
        daily_spend_cap_usd: Number(data.daily_spend_cap_usd || 0),
      });
      setPromptDefaults(data.prompt_defaults || {});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // SEC-001 helper — strip secret fields from the PUT payload if the user
  // didn't touch them (server would otherwise overwrite the real secret
  // with an empty string). A field is "untouched" when the backend
  // returned a masked descriptor for it AND the user hasn't typed
  // anything into the input.
  const SENSITIVE_KEYS = [
    "vapi_api_key", "twilio_auth_token", "quo_api_key", "quo_webhook_secret",
    "sendgrid_api_key", "apollo_api_key", "apify_api_token", "google_places_api_key",
  ];
  const stripUntouchedSecrets = (payload) => {
    const out = { ...payload };
    for (const k of SENSITIVE_KEYS) {
      // If server already told us this is set AND the user typed nothing,
      // omit the key entirely so we don't overwrite.
      if (secretMeta[k]?.set && !out[k]) delete out[k];
    }
    return out;
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", stripUntouchedSecrets(form));
      toast.success("Settings saved");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  const syncVapi = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/settings/sync-vapi");
      toast.success(`Vapi webhook synced → ${data.synced_url.slice(0, 50)}...`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Sync failed");
    } finally { setSyncing(false); }
  };

  const runTestCall = async () => {
    const phone = testPhone.trim();
    if (!phone.startsWith("+") || phone.length < 8) {
      toast.error("Phone must be in E.164 format (e.g. +17131234567)");
      return;
    }
    setTesting(true);
    try {
      const { data } = await api.post("/settings/test-call", { to: phone });
      if (data.mocked) {
        toast.info("Test call mocked (Vapi not fully configured)");
      } else {
        toast.success(`Ringing ${phone}… you should get a call from Vapi in a few seconds`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Test call failed");
    } finally { setTesting(false); }
  };

  const runTestSms = async () => {
    const phone = testSmsPhone.trim();
    if (!phone.startsWith("+") || phone.length < 8) {
      toast.error("Phone must be in E.164 format (e.g. +17131234567)");
      return;
    }
    setTestingSms(true);
    try {
      const { data } = await api.post("/settings/test-sms", { to: phone });
      if (data.mocked) {
        toast.info(`Test SMS mocked — fill in ${data.provider === "quo" ? "Quo" : "Twilio"} credentials to send for real`);
      } else {
        toast.success(`SMS sent via ${data.provider} (id: ${data.id || "—"})`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Test SMS failed");
    } finally { setTestingSms(false); }
  };

  const runTestEmail = async () => {
    const to = testEmail.trim();
    if (!to.includes("@")) {
      toast.error("Enter a valid recipient email");
      return;
    }
    setTestingEmail(true);
    try {
      const { data } = await api.post("/settings/test-email", { to });
      if (data.mocked) {
        toast.info("Test email mocked — add a SendGrid key + from address first");
      } else {
        toast.success(`Email sent (SendGrid id: ${data.id?.slice(0, 16) || "—"})`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Test email failed");
    } finally { setTestingEmail(false); }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(form.webhook_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const savePrompts = async () => {
    setSavingPrompts(true);
    try {
      await api.put("/settings", {
        prompt_system: form.prompt_system,
        prompt_call_script: form.prompt_call_script,
        prompt_sms: form.prompt_sms,
        prompt_email: form.prompt_email,
      });
      toast.success("AI prompts saved — next 'AI Score' will use them");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setSavingPrompts(false); }
  };

  const resetPromptField = (key) => {
    set(key, "");
    toast.info("Reset to default on next save");
  };
  const loadDefaultIntoField = (key) => {
    set(key, promptDefaults[key] || "");
    toast.info("Loaded default into editor — tweak and save");
  };

  const [smartPreview, setSmartPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchSmartPreview = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await api.get("/settings/smart-schedule-preview");
      setSmartPreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load preview");
    } finally { setPreviewLoading(false); }
  };

  // ---- Cadence editor helpers ----
  const updateStep = (idx, patch) => {
    setForm((s) => {
      const next = s.sequence_steps.map((step, i) => (i === idx ? { ...step, ...patch } : step));
      return { ...s, sequence_steps: next };
    });
  };
  const removeStep = (idx) => {
    setForm((s) => ({ ...s, sequence_steps: s.sequence_steps.filter((_, i) => i !== idx) }));
  };
  const addStep = () => {
    setForm((s) => {
      const lastDay = s.sequence_steps.length
        ? Math.max(...s.sequence_steps.map((x) => x.day_offset ?? 0))
        : 0;
      const newStep = {
        step: s.sequence_steps.length + 1,
        day_offset: lastDay + 2,
        hour: 11,
        minute: 0,
        kind: "sms",
        angle: "value",
      };
      return { ...s, sequence_steps: [...s.sequence_steps, newStep] };
    });
  };
  const resetCadence = () => {
    setConfirmModal({
      title: "Reset cadence to default?",
      body: "Your current sequence steps will be replaced with the system default (4 touchpoints across SMS + calls). This is recoverable by editing again.",
      destructive: false,
      confirmLabel: "Reset",
      onConfirm: async () => {
        setForm((s) => ({ ...s, sequence_steps: [] }));
        setSaving(true);
        try {
          await api.put("/settings", { sequence_steps: [] });
          toast.success("Cadence reset to default — reloading");
          await load();
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || "Failed");
        } finally { setSaving(false); }
      },
    });
  };
  const saveCadence = async () => {
    setSaving(true);
    try {
      // Normalize step numbers + sort by day_offset before save.
      const normalized = [...form.sequence_steps]
        .sort((a, b) => (a.day_offset ?? 0) - (b.day_offset ?? 0))
        .map((s, i) => ({ ...s, step: i + 1 }));
      await api.put("/settings", {
        sequence_steps: normalized,
        business_hours_start: form.business_hours_start,
        business_hours_end: form.business_hours_end,
        business_days_only: form.business_days_only,
        smart_scheduling: form.smart_scheduling,
      });
      toast.success("Cadence saved — applies to NEW leads from now on");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 text-sm text-zinc-500 font-mono">Loading…</div>;

  return (
    <div className="p-8 max-w-4xl" data-testid="settings-page">
      <div className="border-b border-zinc-200 pb-6 mb-8">
        <div className="overline mb-2">/ settings</div>
        <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight">Configuration</h1>
        <p className="text-sm text-zinc-600 mt-2">Vapi webhooks, AI auto-sequences, and outreach cadence.</p>
      </div>

      {/* My Profile (per-user) — distinct from workspace-level config */}
      <MyProfileSection />

      {/* Vapi status */}
      <div className="mb-4 border border-zinc-200 bg-white p-5 flex items-center gap-4" data-testid="vapi-status">
        <div className={`w-2 h-2 rounded-full ${settings?.vapi_configured ? "bg-green-500" : "bg-red-500"}`} />
        <div className="flex-1">
          <div className="font-heading font-bold text-sm">
            Vapi.ai (Voice) {settings?.vapi_configured ? "Connected" : "Not configured"}
          </div>
          <div className="text-xs text-zinc-500 font-mono">
            Default Assistant: {settings?.vapi_default_assistant_id || "—"}
          </div>
        </div>
      </div>

      {/* Twilio status */}
      <div className="mb-8 border border-zinc-200 bg-white p-5 flex items-center gap-4" data-testid="twilio-status">
        <div className={`w-2 h-2 rounded-full ${settings?.twilio_configured ? "bg-green-500" : "bg-red-500"}`} />
        <div className="flex-1">
          <div className="font-heading font-bold text-sm">
            Twilio (SMS) {settings?.twilio_configured ? "Connected" : "Not configured"}
          </div>
          <div className="text-xs text-zinc-500 font-mono">
            All `/sms` calls now route through Twilio REST API
          </div>
        </div>
      </div>

      {/* Webhook block */}
      <Section
        title="Vapi Webhook URL"
        subtitle="Point your Vapi Assistant's Server URL here to receive call transcripts and end-of-call reports. When you switch to production, just paste your prod URL here and click Sync."
        icon={<Zap className="w-4 h-4" />}
      >
        <label className="overline block mb-2">Webhook URL</label>
        <div className="flex gap-2 mb-3">
          <input
            value={form.webhook_url}
            onChange={(e) => set("webhook_url", e.target.value)}
            data-testid="input-webhook-url"
            className="flex-1 px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 font-mono"
            placeholder="https://your-prod-domain.com/api/webhooks/vapi"
          />
          <button
            type="button"
            onClick={copyUrl}
            data-testid="btn-copy-webhook"
            className="px-3 border border-zinc-300 hover:bg-zinc-50 text-xs font-bold uppercase tracking-widest flex items-center gap-1"
          >
            {copied ? <><Check className="w-3.5 h-3.5 text-green-600" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
          </button>
        </div>

        <div className="text-xs text-zinc-500 mb-4">
          Default: <span className="font-mono">{settings?.default_webhook_url}</span>
        </div>

        <label className="overline block mb-2">AI Agent Name</label>
        <input
          value={form.agent_name}
          onChange={(e) => set("agent_name", e.target.value)}
          data-testid="input-agent-name"
          className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 mb-2"
          placeholder="Sara"
          maxLength={40}
        />
        <p className="text-xs text-zinc-500 mb-4">
          The name your AI uses on calls, SMS and emails. Replaces every <span className="font-mono">[Your Name]</span> placeholder. Tip: pick something short and friendly (e.g. <span className="font-mono">Sara</span>, <span className="font-mono">Alex</span>, <span className="font-mono">Mike</span>).
        </p>

        <label className="overline block mb-2">Vapi Assistant ID</label>
        <input
          value={form.vapi_assistant_id}
          onChange={(e) => set("vapi_assistant_id", e.target.value)}
          data-testid="input-assistant-id"
          className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 font-mono mb-4"
          placeholder="uuid…"
        />

        <label className="overline block mb-2">Vapi Phone Number ID</label>
        <input
          value={form.vapi_phone_number_id}
          onChange={(e) => set("vapi_phone_number_id", e.target.value)}
          data-testid="input-phone-number-id"
          className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 font-mono mb-2"
          placeholder="uuid of your Twilio-linked Vapi number…"
        />
        <p className="text-xs text-zinc-500 mb-3">
          Get this from Vapi → Phone Numbers. Use a number you <strong>imported from Twilio</strong> — Vapi-provided numbers have a daily outbound call limit.
        </p>

        <label className="overline block mb-2 mt-4">Apollo.io API Key <span className="text-zinc-400 normal-case font-normal">(for B2B lead source)</span></label>
        <input
          value={form.apollo_api_key}
          onChange={(e) => set("apollo_api_key", e.target.value)}
          data-testid="input-apollo-api-key"
          className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 font-mono mb-2"
          placeholder={secretMeta.apollo_api_key?.set ? `Saved · ••••${secretMeta.apollo_api_key.last4} (type to replace)` : "apkey_xxx... (leave blank to use APOLLO_API_KEY env var)"}
          type="password"
          autoComplete="off"
        />
        <p className="text-xs text-zinc-500 mb-3">
          Enables the <strong>Apollo · B2B Contacts</strong> source on the Scraper page. Get yours at <a href="https://app.apollo.io/#/settings/integrations/api" target="_blank" rel="noreferrer" className="underline text-[#002FA7]">apollo.io → Settings → API</a>. Apollo charges credits per email/phone reveal — keep your filters tight.
        </p>

        <label className="overline block mb-2 mt-4">Apify API Token <span className="text-zinc-400 normal-case font-normal">(for marketplace scrapers)</span></label>
        <input
          value={form.apify_api_token}
          onChange={(e) => set("apify_api_token", e.target.value)}
          data-testid="input-apify-api-token"
          className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 font-mono mb-2"
          placeholder={secretMeta.apify_api_token?.set ? `Saved · ••••${secretMeta.apify_api_token.last4} (type to replace)` : "apify_api_xxxxxxxxxxxxxxxxxxxxxxxxxxxx"}
          type="password"
          autoComplete="off"
        />
        <p className="text-xs text-zinc-500 mb-3">
          Enables the <strong>Apify · Marketplace</strong> source on the Scraper page. Get yours at <a href="https://console.apify.com/account/integrations" target="_blank" rel="noreferrer" className="underline text-[#002FA7]">console.apify.com → Account → Integrations</a>. Lets you import any finished dataset OR run any actor (Google Maps, LinkedIn, Yelp, custom) without leaving the CRM.
        </p>

        {/* Test Call */}
        <div className="mb-4">
          {!testCallOpen ? (
            <button
              type="button"
              onClick={() => setTestCallOpen(true)}
              data-testid="btn-test-call-open"
              className="text-xs font-bold uppercase tracking-widest text-[#002FA7] hover:text-[#00227A] inline-flex items-center gap-1.5"
            >
              <Phone className="w-3.5 h-3.5" /> Send me a test call
            </button>
          ) : (
            <div className="border-l-2 border-[#002FA7] bg-blue-50/40 pl-4 py-3" data-testid="test-call-panel">
              <div className="text-xs font-bold uppercase tracking-widest text-zinc-700 mb-2 flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5" /> Vapi Test Call
              </div>
              <p className="text-xs text-zinc-600 mb-3">
                Vapi will call this number using the assistant + phone number ID above. Use it after every config change to confirm everything works.
              </p>
              <div className="flex gap-2">
                <input
                  type="tel"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  data-testid="input-test-phone"
                  placeholder="+17131234567"
                  className="flex-1 px-3 py-2 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] font-mono bg-white"
                />
                <button
                  type="button"
                  onClick={runTestCall}
                  disabled={testing || !testPhone}
                  data-testid="btn-test-call-run"
                  className="bg-[#002FA7] hover:bg-[#00227A] text-white px-4 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-50"
                >
                  {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Phone className="w-3.5 h-3.5" />}
                  Call Me
                </button>
                <button
                  type="button"
                  onClick={() => setTestCallOpen(false)}
                  data-testid="btn-test-call-close"
                  className="px-3 py-2 text-xs font-bold uppercase tracking-widest text-zinc-500 hover:text-zinc-900"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="btn-save-settings"
            className="bg-[#002FA7] hover:bg-[#00227A] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save
          </button>
          <button
            type="button"
            onClick={syncVapi}
            disabled={syncing || !form.webhook_url}
            data-testid="btn-sync-vapi"
            className="bg-black hover:bg-zinc-800 text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
          >
            {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Sync to Vapi Assistant
          </button>
        </div>
      </Section>

      <SequencesSection
        form={form}
        set={set}
        save={save}
        saving={saving}
        updateStep={updateStep}
        removeStep={removeStep}
        addStep={addStep}
        resetCadence={resetCadence}
        saveCadence={saveCadence}
        smartPreview={smartPreview}
        previewLoading={previewLoading}
        fetchSmartPreview={fetchSmartPreview}
      />

      <Section
        title="Auto-Qualify (AI reads replies + call transcripts)"
        subtitle="When a prospect replies to an SMS or the Vapi call ends with a transcript, Claude classifies the intent. In 'Suggest' mode we surface a banner on the lead and you accept it manually. Switch to 'Auto' once you trust the classifier."
        icon={<Shield className="w-4 h-4" />}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="auto-qualify-grid">
          {[
            { value: "off", label: "Off", desc: "Don't classify replies." },
            { value: "suggest", label: "Suggest", desc: "AI proposes — you confirm." },
            { value: "auto", label: "Auto", desc: "Move stage immediately on positive intent." },
          ].map((opt) => (
            <label
              key={opt.value}
              className={`border p-3 cursor-pointer transition-colors ${
                form.auto_qualify_mode === opt.value
                  ? "border-[#002FA7] bg-blue-50/40"
                  : "border-zinc-200 hover:border-zinc-400"
              }`}
              data-testid={`auto-qualify-${opt.value}`}
            >
              <input
                type="radio"
                name="auto_qualify_mode"
                value={opt.value}
                checked={form.auto_qualify_mode === opt.value}
                onChange={() => set("auto_qualify_mode", opt.value)}
                className="sr-only"
              />
              <div className="text-sm font-bold uppercase tracking-wider">{opt.label}</div>
              <div className="text-xs text-zinc-500 mt-1">{opt.desc}</div>
            </label>
          ))}
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="btn-save-auto-qualify"
          className="mt-5 bg-[#002FA7] hover:bg-[#00227A] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save mode
        </button>
        <p className="mt-3 text-xs text-zinc-500">
          Auto mode acts on confidence ≥ 60%. <code>interested</code> → moves to <code>qualified</code>; <code>not_interested</code> / <code>unsubscribe</code> → moves to <code>lost</code>. Sequence pauses on every applied move.
        </p>
      </Section>

      <Section
        title="Google Calendar (AI demo booking)"
        subtitle="Connect a Google account so the Vapi voice agent can book demos directly onto your calendar when a prospect says 'yes'. Slots respect the working window below; existing events are treated as busy."
        icon={<CalendarIcon className="w-4 h-4" />}
      >
        <div
          className="border border-zinc-200 bg-white p-4 mb-5 flex items-start gap-4"
          data-testid="gcal-connection-block"
        >
          <div
            className={`w-2.5 h-2.5 rounded-full mt-2 ${
              gcalStatus?.connected ? "bg-green-500" : gcalStatus?.configured ? "bg-amber-500" : "bg-red-500"
            }`}
            data-testid="gcal-status-dot"
          />
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-sm" data-testid="gcal-status-text">
              {gcalStatus === null
                ? "Loading…"
                : !gcalStatus.configured
                ? "Google OAuth is not configured on the server"
                : gcalStatus.connected
                ? `Connected: ${gcalStatus.google_email}`
                : "Not connected"}
            </div>
            <div className="text-xs text-zinc-500 font-mono mt-1 break-all">
              {gcalStatus?.connected
                ? `Since ${gcalStatus.connected_at?.slice(0, 16).replace("T", " ") || "—"}`
                : "The AI agent cannot create real calendar events until you connect."}
            </div>
          </div>
          {gcalStatus?.configured && !gcalStatus?.connected && (
            <button
              type="button"
              onClick={connectGoogleCalendar}
              disabled={gcalLoading}
              data-testid="btn-gcal-connect"
              className="bg-[#002FA7] hover:bg-[#00227A] text-white px-4 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-50 shrink-0"
            >
              {gcalLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
              Connect Google Calendar
            </button>
          )}
          {gcalStatus?.connected && (
            <button
              type="button"
              onClick={disconnectGoogleCalendar}
              disabled={gcalLoading}
              data-testid="btn-gcal-disconnect"
              className="border border-zinc-300 hover:bg-red-50 hover:border-red-300 hover:text-red-700 text-zinc-700 px-4 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-50 shrink-0"
            >
              {gcalLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unplug className="w-3.5 h-3.5" />}
              Disconnect
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5" data-testid="gcal-prefs-grid">
          <Field label="Meeting duration (min)">
            <input
              type="number"
              min={15}
              max={240}
              step={15}
              value={form.calendar_meeting_duration_min}
              onChange={(e) => set("calendar_meeting_duration_min", Number(e.target.value))}
              data-testid="input-gcal-duration"
              className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] font-mono"
            />
          </Field>
          <Field label="Working hours start (24h)">
            <input
              type="number"
              min={0}
              max={23}
              value={form.calendar_work_start_hour}
              onChange={(e) => set("calendar_work_start_hour", Number(e.target.value))}
              data-testid="input-gcal-start"
              className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] font-mono"
            />
          </Field>
          <Field label="Working hours end (24h)">
            <input
              type="number"
              min={1}
              max={24}
              value={form.calendar_work_end_hour}
              onChange={(e) => set("calendar_work_end_hour", Number(e.target.value))}
              data-testid="input-gcal-end"
              className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] font-mono"
            />
          </Field>
        </div>

        <Field label="Working days">
          <div className="flex flex-wrap gap-2 mt-1" data-testid="gcal-workdays-grid">
            {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((label, idx) => {
              const active = (form.calendar_workdays || []).includes(idx);
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => toggleWorkday(idx)}
                  data-testid={`gcal-workday-${idx}`}
                  className={`px-3 py-2 text-xs font-bold uppercase tracking-wider border ${
                    active
                      ? "bg-[#002FA7] border-[#002FA7] text-white"
                      : "border-zinc-300 text-zinc-600 hover:border-zinc-400 bg-white"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </Field>

        <div className="mt-5">
          <Field label="Timezone (IANA)">
            <input
              type="text"
              value={form.calendar_timezone}
              onChange={(e) => set("calendar_timezone", e.target.value)}
              data-testid="input-gcal-timezone"
              placeholder="America/New_York"
              className="w-full md:w-80 px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] font-mono"
            />
          </Field>
          <p className="text-xs text-zinc-500 mt-1.5">
            Used when describing available slots to prospects. Example: <span className="font-mono">America/Mexico_City</span>, <span className="font-mono">Europe/Madrid</span>.
          </p>
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={saveCalendarPrefs}
            disabled={saving}
            data-testid="btn-gcal-save-prefs"
            className="bg-[#002FA7] hover:bg-[#00227A] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save booking preferences
          </button>
          <a
            href="/book/preview"
            target="_blank"
            rel="noreferrer"
            data-testid="btn-preview-booking-page"
            className="border border-zinc-300 hover:border-[#002FA7] hover:text-[#002FA7] text-zinc-800 px-5 py-2.5 text-xs font-bold uppercase tracking-widest flex items-center gap-2"
          >
            <CalendarIcon className="w-3.5 h-3.5" />
            Preview booking page
          </a>
        </div>
      </Section>

      {/* ──────────────── Daily Spend Cap (cost ceiling) ──────────────── */}
      <Section
        title="Daily Spend Cap"
        subtitle="Hard ceiling on combined Twilio/Vapi/Claude/SendGrid spend across this workspace in any 24h window. Outbound SMS, calls and emails refuse to dispatch once the cap is reached. Set to 0 to disable."
        icon={<DollarSign className="w-4 h-4" />}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
          <Field label="Daily cap (USD)" hint="Recommended: $50–$200 while ramping. Raise after a week of clean spend.">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-zinc-500">$</span>
              <input
                type="number" min="0" step="1"
                className="w-full pl-7 pr-3 py-2 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7]"
                value={form.daily_spend_cap_usd || 0}
                onChange={(e) => set("daily_spend_cap_usd", Math.max(0, parseFloat(e.target.value || "0")))}
                data-testid="settings-daily-spend-cap"
              />
            </div>
          </Field>
          <SpendTodayWidget />
        </div>
      </Section>

      {/* ──────────────── Maintenance (one-shot ops tasks) ──────────────── */}
      <Section
        title="Maintenance"
        subtitle="One-click backfills and schema migrations. Safe to run anytime — every action shows a dry-run preview first."
        icon={<Wrench className="w-4 h-4" />}
      >
        <BackfillStagesWidget />
      </Section>

      <IntegrationsSection
        form={form}
        set={set}
        save={save}
        saving={saving}
        settings={settings}
        secretMeta={secretMeta}
        testSmsPhone={testSmsPhone}
        setTestSmsPhone={setTestSmsPhone}
        runTestSms={runTestSms}
        testingSms={testingSms}
        testEmail={testEmail}
        setTestEmail={setTestEmail}
        runTestEmail={runTestEmail}
        testingEmail={testingEmail}
      />

      {/* ──────────────── Custom AI Angles ──────────────── */}
      <CustomAnglesSection
        angles={form.custom_angles}
        onChange={(angles) => set("custom_angles", angles)}
        saving={saving}
        onSave={save}
      />


      <Section
        title="AI Prompts"
        subtitle="Override the prompts your AI uses to generate call scripts, SMS and email. Leave a field blank to use the default. You can reference {agent_name}, {product_desc}, {value_prop} — they'll be replaced at runtime."
        icon={<Sparkles className="w-4 h-4" />}
      >
        <PromptField
          label="System prompt (AI persona)"
          testid="prompt-system"
          field="prompt_system"
          value={form.prompt_system}
          defaultText={promptDefaults.prompt_system}
          onChange={(v) => set("prompt_system", v)}
          onReset={() => resetPromptField("prompt_system")}
          onLoadDefault={() => loadDefaultIntoField("prompt_system")}
          rows={3}
          hint="High-level role for Claude. Default: elite B2B sales strategist."
        />
        <PromptField
          label="Call script rules (opening line)"
          testid="prompt-call-script"
          field="prompt_call_script"
          value={form.prompt_call_script}
          defaultText={promptDefaults.prompt_call_script}
          onChange={(v) => set("prompt_call_script", v)}
          onReset={() => resetPromptField("prompt_call_script")}
          onLoadDefault={() => loadDefaultIntoField("prompt_call_script")}
          rows={5}
          hint="This controls how the AI opens cold calls. Be specific — words, length, tone."
        />
        <PromptField
          label="SMS rules"
          testid="prompt-sms"
          field="prompt_sms"
          value={form.prompt_sms}
          defaultText={promptDefaults.prompt_sms}
          onChange={(v) => set("prompt_sms", v)}
          onReset={() => resetPromptField("prompt_sms")}
          onLoadDefault={() => loadDefaultIntoField("prompt_sms")}
          rows={3}
          hint="Length, tone, CTA. Always under 320 chars."
        />
        <PromptField
          label="Email rules"
          testid="prompt-email"
          field="prompt_email"
          value={form.prompt_email}
          defaultText={promptDefaults.prompt_email}
          onChange={(v) => set("prompt_email", v)}
          onReset={() => resetPromptField("prompt_email")}
          onLoadDefault={() => loadDefaultIntoField("prompt_email")}
          rows={3}
          hint="Subject + body length, signature."
        />
        <div className="flex items-center gap-3 pt-2">
          <button
            type="button"
            onClick={savePrompts}
            disabled={savingPrompts}
            data-testid="btn-save-prompts"
            className="bg-[#002FA7] hover:bg-[#00227A] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
          >
            {savingPrompts ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save Prompts
          </button>
          <span className="text-xs text-zinc-500">
            Re-classify a lead from its detail page to test new prompts.
          </span>
        </div>
      </Section>

      <BookingSection
        form={form}
        set={set}
        save={save}
        saving={saving}
        settings={settings}
      />

      <ConfirmModal modal={confirmModal} onClose={() => setConfirmModal(null)} />
    </div>
  );
}

function SpendTodayWidget() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let mounted = true;
    api.get("/spend/today")
      .then((r) => { if (mounted) setData(r.data); })
      .catch(() => { if (mounted) setData({ error: true }); });
    return () => { mounted = false; };
  }, []);
  if (!data) return (
    <div className="border border-zinc-200 p-4 text-xs text-zinc-500" data-testid="spend-today-loading">
      Loading 24h spend…
    </div>
  );
  if (data.error) return null;
  const pct = data.cap_usd > 0 ? Math.min(100, (data.spent_usd / data.cap_usd) * 100) : 0;
  const barColor = pct >= 100 ? "bg-rose-600" : pct >= 75 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="border border-zinc-200 p-4" data-testid="spend-today-widget">
      <div className="overline mb-2">Last 24h spend</div>
      <div className="flex items-baseline gap-2 mb-2">
        <div className="text-3xl font-black tracking-tight">${data.spent_usd.toFixed(2)}</div>
        {data.uncapped ? (
          <div className="text-xs text-zinc-500">/ uncapped</div>
        ) : (
          <div className="text-xs text-zinc-500">/ ${data.cap_usd.toFixed(2)} cap</div>
        )}
      </div>
      {!data.uncapped && (
        <div className="h-2 bg-zinc-100 overflow-hidden">
          <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
        </div>
      )}
      {data.capped && (
        <div className="mt-2 text-xs text-rose-700 font-bold uppercase tracking-wider" data-testid="spend-capped-warning">
          Cap reached — outbound paused
        </div>
      )}
    </div>
  );
}

// ──────────────────── Backfill new pipeline stages ──────────────────────
// One-click button that hits `POST /api/ops/backfill-stages`. Defaults to a
// dry-run so the user sees what would change BEFORE committing.
function BackfillStagesWidget() {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);   // dry-run response
  const [applied, setApplied] = useState(null);   // committed response
  const [error, setError] = useState(null);

  async function runDryRun() {
    setLoading(true); setError(null); setApplied(null);
    try {
      const r = await api.post("/ops/backfill-stages");   // dry_run defaults true
      setPreview(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Backfill failed");
    } finally {
      setLoading(false);
    }
  }

  async function apply() {
    if (!window.confirm("Apply backfill? This will move matching leads to the new stages and write audit activities.")) return;
    setLoading(true); setError(null);
    try {
      const r = await api.post("/ops/backfill-stages?dry_run=false");
      setApplied(r.data);
      toast.success(
        `Moved ${r.data.stats.moved_to_answered} → answered, ${r.data.stats.moved_to_booked} → booked`
      );
      setPreview(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Backfill failed");
    } finally {
      setLoading(false);
    }
  }

  const stats = applied?.stats || preview?.stats;
  const movedTotal = stats ? stats.moved_to_answered + stats.moved_to_booked : 0;

  return (
    <div className="border border-zinc-200 p-5" data-testid="backfill-stages-widget">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="font-bold text-sm">Backfill pipeline stages</div>
          <p className="text-xs text-zinc-600 mt-1 max-w-xl">
            Apply the new <code className="px-1 bg-zinc-100">Answered</code> and{" "}
            <code className="px-1 bg-zinc-100">Booked</code> stages retroactively to
            existing leads. Inbound replies → Answered. Has a booking → Booked.
            Never regresses a lead. Idempotent.
          </p>
        </div>
        <button
          onClick={runDryRun}
          disabled={loading}
          data-testid="backfill-dry-run-btn"
          className="shrink-0 px-3 py-2 text-xs font-bold uppercase tracking-widest border border-zinc-300 hover:border-[#002FA7] hover:text-[#002FA7] flex items-center gap-1.5 disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {preview ? "Re-scan" : "Scan leads"}
        </button>
      </div>

      {error && (
        <div className="mt-2 text-xs text-rose-700 flex items-center gap-1.5" data-testid="backfill-error">
          <AlertTriangle className="w-3.5 h-3.5" /> {error}
        </div>
      )}

      {stats && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="backfill-stats">
          <Stat label="Inbound-active" value={stats.inbound_active_leads} />
          <Stat label="Has booking" value={stats.booked_leads} />
          <Stat label="→ Answered" value={stats.moved_to_answered} highlight />
          <Stat label="→ Booked" value={stats.moved_to_booked} highlight />
        </div>
      )}

      {preview && !applied && movedTotal > 0 && (
        <div className="mt-4 border-t border-zinc-200 pt-4">
          <div className="text-xs text-zinc-600 mb-3">
            Dry-run complete. {movedTotal} lead{movedTotal === 1 ? "" : "s"} will be moved.
          </div>
          {(preview.preview.to_answered.length + preview.preview.to_booked.length) > 0 && (
            <details className="mb-3 text-xs">
              <summary className="cursor-pointer text-zinc-700 hover:text-[#002FA7]">
                Show preview ({preview.preview.to_answered.length + preview.preview.to_booked.length} sample)
              </summary>
              <div className="mt-2 space-y-1 max-h-48 overflow-y-auto pl-3 border-l-2 border-zinc-100">
                {preview.preview.to_booked.map((l) => (
                  <div key={`b-${l.id}`} className="text-zinc-600">
                    <span className="font-mono">{l.from_stage}</span> → <span className="font-bold text-purple-700">booked</span> · {l.name || "(unnamed)"}
                  </div>
                ))}
                {preview.preview.to_answered.map((l) => (
                  <div key={`a-${l.id}`} className="text-zinc-600">
                    <span className="font-mono">{l.from_stage}</span> → <span className="font-bold text-cyan-700">answered</span> · {l.name || "(unnamed)"}
                  </div>
                ))}
              </div>
            </details>
          )}
          <button
            onClick={apply}
            disabled={loading}
            data-testid="backfill-apply-btn"
            className="px-4 py-2 text-xs font-bold uppercase tracking-widest bg-[#002FA7] hover:bg-[#00227A] text-white flex items-center gap-1.5 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Apply backfill
          </button>
        </div>
      )}

      {preview && !applied && movedTotal === 0 && (
        <div className="mt-3 text-xs text-emerald-700 font-medium" data-testid="backfill-nothing-to-do">
          ✓ Nothing to migrate — all leads are already on the new schema.
        </div>
      )}

      {applied && (
        <div className="mt-3 text-xs text-emerald-700 font-medium" data-testid="backfill-applied">
          ✓ Backfill applied. {applied.stats.moved_to_answered + applied.stats.moved_to_booked} lead(s) updated.
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, highlight = false }) {
  return (
    <div className="border border-zinc-200 p-2.5">
      <div className="overline text-[10px]">{label}</div>
      <div className={`text-2xl font-black tracking-tight mt-0.5 ${highlight ? "text-[#002FA7]" : "text-zinc-900"}`}>
        {value}
      </div>
    </div>
  );
}

// ──────────────────────── My Profile (per-user) ────────────────────────
// Personal preferences that travel with the human user, NOT the workspace.
// Right now we expose just the timezone — used when this user publishes
// their personal booking link (so an Austin user gets 9-6 Central even if
// the workspace admin is in Madrid).
const PROFILE_TZ_LIST = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Phoenix", "America/Anchorage", "Pacific/Honolulu",
  "America/Toronto", "America/Vancouver",
  "America/Mexico_City", "America/Bogota", "America/Lima", "America/Santiago",
  "America/Buenos_Aires", "America/Sao_Paulo",
  "Europe/London", "Europe/Dublin", "Europe/Madrid", "Europe/Paris",
  "Europe/Berlin", "Europe/Amsterdam", "Europe/Rome", "Europe/Athens",
  "Europe/Istanbul", "Africa/Cairo", "Africa/Johannesburg",
  "Asia/Dubai", "Asia/Kolkata", "Asia/Bangkok", "Asia/Shanghai",
  "Asia/Singapore", "Asia/Tokyo", "Asia/Seoul",
  "Australia/Sydney", "Australia/Melbourne", "Pacific/Auckland",
  "UTC",
];

function MyProfileSection() {
  const [me, setMe] = useState(null);
  const [tz, setTz] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const browserTz = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return ""; }
  })();

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      setMe(r.data);
      setTz(r.data.timezone || "");
      setName(r.data.name || "");
    }).catch(() => {});
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await api.patch("/auth/me", patch);
      setMe(r.data);
      toast.success("Profile saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  if (!me) return null;
  const tzOptions = [tz, browserTz, ...PROFILE_TZ_LIST].filter(Boolean);
  const seen = new Set();
  const tzListUnique = tzOptions.filter((t) => (seen.has(t) ? false : (seen.add(t), true)));

  return (
    <div className="mb-8 border border-zinc-200 bg-white p-6" data-testid="my-profile-section">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="overline mb-1">YOUR PROFILE</div>
          <h2 className="font-bold text-lg text-zinc-900">{me.email}</h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Personal preferences — apply to your booking link and the times you see across the app.
          </p>
        </div>
        {saving && <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="overline block mb-2">Your name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => { if (name !== me.name) save({ name }); }}
            data-testid="profile-name-input"
            className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7]"
          />
        </div>
        <div>
          <label className="overline block mb-2">Your timezone</label>
          <select
            value={tz}
            onChange={(e) => {
              setTz(e.target.value);
              save({ timezone: e.target.value });
            }}
            data-testid="profile-timezone-select"
            className="w-full px-3 py-2.5 border border-zinc-300 text-sm bg-white focus:outline-none focus:border-[#002FA7] font-mono"
          >
            <option value="">— Use workspace default —</option>
            {tzListUnique.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <div className="text-[11px] text-zinc-500 mt-1.5">
            Detected from browser: <span className="font-mono">{browserTz || "—"}</span>
            {browserTz && tz !== browserTz && (
              <button
                type="button"
                onClick={() => { setTz(browserTz); save({ timezone: browserTz }); }}
                data-testid="profile-tz-use-browser"
                className="ml-2 underline hover:text-zinc-700"
              >
                Use this
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}



function PromptField({ label, testid, field, value, defaultText, onChange, onReset, onLoadDefault, rows = 4, hint }) {
  const isCustom = (value || "").trim().length > 0;
  return (
    <div className="mb-5" data-testid={`field-${testid}`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <label className="overline">{label}</label>
        <div className="flex items-center gap-2">
          <span
            className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${
              isCustom ? "bg-amber-100 text-amber-800" : "bg-zinc-100 text-zinc-500"
            }`}
            data-testid={`badge-${testid}`}
          >
            {isCustom ? "Custom" : "Default"}
          </span>
          <button
            type="button"
            onClick={onLoadDefault}
            data-testid={`btn-load-default-${testid}`}
            className="text-[10px] font-bold uppercase tracking-wider text-zinc-600 hover:text-[#002FA7] flex items-center gap-1"
            title="Load the default text into the editor so you can tweak from it"
          >
            <Sparkles className="w-3 h-3" /> Load default
          </button>
          {isCustom && (
            <button
              type="button"
              onClick={onReset}
              data-testid={`btn-reset-${testid}`}
              className="text-[10px] font-bold uppercase tracking-wider text-zinc-600 hover:text-red-600 flex items-center gap-1"
              title="Clear the override → uses built-in default again on next save"
            >
              <RotateCcw className="w-3 h-3" /> Reset
            </button>
          )}
        </div>
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        data-testid={`textarea-${testid}`}
        placeholder={defaultText || "Leave blank to use default"}
        className="w-full px-3 py-2.5 border border-zinc-300 text-sm font-mono leading-relaxed focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 resize-y"
      />
      {hint && <p className="text-xs text-zinc-500 mt-1">{hint}</p>}
    </div>
  );
}

function CustomAnglesSection({ angles, onChange, saving, onSave }) {
  const addAngle = () => {
    onChange([
      ...(angles || []),
      { key: "", label: "", directive: "" },
    ]);
  };
  const updateAt = (idx, patch) => {
    const next = angles.map((a, i) => (i === idx ? { ...a, ...patch } : a));
    onChange(next);
  };
  const removeAt = (idx) => onChange(angles.filter((_, i) => i !== idx));

  const builtIns = [
    { key: "intro",           label: "Intro",           hint: "Touch #1, warm and curious" },
    { key: "value",           label: "Value",           hint: "Touch #3, brings a proof point / case study" },
    { key: "breakup",         label: "Breakup",         hint: "Soft polite stop-or-go message" },
    { key: "first_attempt",   label: "First call",      hint: "30s opener, permission hook" },
    { key: "second_attempt",  label: "Second call",     hint: "Acknowledge prior attempt, reframe" },
    { key: "last_call",       label: "Last call",       hint: "Final attempt, no pressure" },
  ];

  return (
    <div className="mb-8 border border-zinc-200 bg-white" data-testid="custom-angles-section">
      <div className="px-5 py-4 border-b border-zinc-200 flex items-start gap-3">
        <div className="w-8 h-8 bg-[#002FA7] text-white flex items-center justify-center shrink-0 mt-0.5">
          <Wand2 className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <h2 className="font-heading text-lg font-bold">Custom AI Angles</h2>
          <p className="text-sm text-zinc-500 mt-1">
            Define your own sequence-step "angles". Each angle is a directive injected into Claude's prompt when a step uses that angle key. Use them to encode strategies like "mention bakery case study" or "lead with $1M+ AR proof".
          </p>
        </div>
      </div>
      <div className="p-5 space-y-5">
        <div className="border border-dashed border-zinc-300 bg-zinc-50/60 p-4 text-xs text-zinc-600">
          <div className="font-bold uppercase tracking-wider text-zinc-700 mb-2">Built-in angles (always available)</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2" data-testid="builtin-angles">
            {builtIns.map((b) => (
              <div key={b.key} className="bg-white border border-zinc-200 px-3 py-2">
                <div className="font-mono text-[11px] text-[#002FA7]">{b.key}</div>
                <div className="text-[11px] text-zinc-500">{b.hint}</div>
              </div>
            ))}
          </div>
          <p className="mt-3">Custom angles override a built-in if you reuse the same key.</p>
        </div>

        <div className="space-y-3" data-testid="custom-angles-list">
          {(angles || []).map((a, idx) => (
            <div key={idx} className="border border-zinc-200 p-3" data-testid={`custom-angle-row-${idx}`}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Key (no spaces)</label>
                  <input
                    type="text"
                    value={a.key}
                    onChange={(e) => updateAt(idx, { key: e.target.value.replace(/[^a-zA-Z0-9_\-]/g, "-") })}
                    placeholder="case-study-bakery"
                    data-testid={`input-angle-key-${idx}`}
                    className="w-full px-2 py-1.5 border border-zinc-300 text-xs font-mono focus:outline-none focus:border-[#002FA7]"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Display label</label>
                  <input
                    type="text"
                    value={a.label}
                    onChange={(e) => updateAt(idx, { label: e.target.value })}
                    placeholder="Case study: bakery (10x leads)"
                    data-testid={`input-angle-label-${idx}`}
                    className="w-full px-2 py-1.5 border border-zinc-300 text-xs focus:outline-none focus:border-[#002FA7]"
                  />
                </div>
              </div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">AI directive (injected into the prompt)</label>
              <textarea
                value={a.directive}
                onChange={(e) => updateAt(idx, { directive: e.target.value })}
                rows={3}
                placeholder="Lead with the 'Sweet Crumb Bakery' case study — 10x more booked tastings in 6 weeks. Keep it concrete, mention one number."
                data-testid={`input-angle-directive-${idx}`}
                className="w-full px-2 py-2 border border-zinc-300 text-xs font-mono leading-relaxed focus:outline-none focus:border-[#002FA7] resize-y"
              />
              <div className="flex justify-end mt-2">
                <button
                  type="button"
                  onClick={() => removeAt(idx)}
                  data-testid={`btn-remove-angle-${idx}`}
                  className="text-[10px] font-bold uppercase tracking-wider text-red-600 hover:text-red-800 flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </div>
            </div>
          ))}
          {(!angles || angles.length === 0) && (
            <div className="text-xs text-zinc-400 italic text-center py-6 border border-dashed border-zinc-200">
              No custom angles yet. Click "Add angle" to create one.
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 pt-2">
          <button
            type="button" onClick={addAngle}
            data-testid="btn-add-custom-angle"
            className="border border-zinc-300 hover:border-[#002FA7] text-zinc-800 hover:text-[#002FA7] px-4 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-2"
          >
            <Plus className="w-3.5 h-3.5" /> Add angle
          </button>
          <button
            type="button" onClick={onSave} disabled={saving}
            data-testid="btn-save-custom-angles"
            className="ml-auto bg-[#002FA7] hover:bg-[#00227A] text-white px-5 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save angles
          </button>
        </div>
      </div>
    </div>
  );
}

